import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { formatNumber, formatAddress, PLANS } from '../utils/constants';
import '../styles/AdminPanel.css';

const AdminPanel = ({ 
  stakingContract, 
  tokenContract, 
  currentNetwork, 
  refreshData, 
  setLoading, 
  loading 
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [adminStats, setAdminStats] = useState(null);
  const [blacklistedUsers, setBlacklistedUsers] = useState([]);
  const [searchUserId, setSearchUserId] = useState('');
  const [searchedUser, setSearchedUser] = useState(null);
  const [contractBalance, setContractBalance] = useState('0');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [promotionalRewardsEnabled, setPromotionalRewardsEnabled] = useState(true);
  const [currentTokenPrice, setCurrentTokenPrice] = useState('0');
  const [riaReferralPercent, setRiaReferralPercent] = useState('30');
  const [riaThresholdMultiplier, setRiaThresholdMultiplier] = useState('10');

  // Form states
  const [planUpdateForm, setPlanUpdateForm] = useState({ planId: 1, roiPercentage: '' });
  const [commissionForm, setCommissionForm] = useState({ planId: 1, commission: '' });
  const [levelCommissionForm, setLevelCommissionForm] = useState({ level: 1, percentage: '' });
  const [blacklistForm, setBlacklistForm] = useState({ userId: '', action: true });
  const [tokenPriceForm, setTokenPriceForm] = useState({ price: '' });
  const [emergencyForm, setEmergencyForm] = useState({ amount: '' });
  const [userRoiForm, setUserRoiForm] = useState({ userId: '', enabled: true });
  const [planToggleForm, setPlanToggleForm] = useState({ planId: 1, isActive: true });
  const [riaSettingsForm, setRiaSettingsForm] = useState({ referralPercent: '', thresholdMultiplier: '' });

  useEffect(() => {
    if (stakingContract && tokenContract) {
      loadAdminData();
    }
  }, [stakingContract, tokenContract]);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      
      const [stats, blacklisted, balance, promoEnabled, tokenPrice, riaPercent, riaMultiplier] = await Promise.allSettled([
        stakingContract.getSystemStats(),
        stakingContract.getBlacklistedUsers(),
        tokenContract.balanceOf(currentNetwork.contracts.staking),
        stakingContract.promotionalRewardsEnabled(),
        stakingContract.getTokenPrice(),
        stakingContract.riaReferralPercent(),
        stakingContract.riaThresholdMultiplier()
      ]);

      if (stats.status === 'fulfilled') {
        setAdminStats({
          totalUsers: stats.value[0].toString(),
          totalStaked: ethers.utils.formatEther(stats.value[1]),
          totalPlans: stats.value[2].toString(),
          activeUsers: stats.value[3].toString()
        });
      }

      if (blacklisted.status === 'fulfilled') {
        setBlacklistedUsers(blacklisted.value.map(id => id.toString()));
      }

      if (balance.status === 'fulfilled') {
        setContractBalance(ethers.utils.formatEther(balance.value));
      }

      if (promoEnabled.status === 'fulfilled') {
        setPromotionalRewardsEnabled(promoEnabled.value);
      }

      if (tokenPrice.status === 'fulfilled') {
        setCurrentTokenPrice(ethers.utils.formatEther(tokenPrice.value));
      }

      if (riaPercent.status === 'fulfilled') {
        setRiaReferralPercent(riaPercent.value.toString());
      }

      if (riaMultiplier.status === 'fulfilled') {
        setRiaThresholdMultiplier(riaMultiplier.value.toString());
      }

    } catch (error) {
      console.error('Failed to load admin data:', error);
      setError('Failed to load admin data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(''), 5000);
  };

  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(''), 5000);
  };

  const searchUser = async () => {
    if (!searchUserId.trim() || !stakingContract) return;

    try {
      setLoading(true);
      setError('');

      // Check if user exists first
      const userExists = await stakingContract.userIdExists(searchUserId);
      if (!userExists) {
        setError('User does not exist');
        setSearchedUser(null);
        return;
      }

      const [userInfo, investments, incomes] = await Promise.all([
        stakingContract.getUserInfo(searchUserId),
        stakingContract.getUserInvestments(searchUserId),
        stakingContract.getUserIncomeBreakdown(searchUserId)
      ]);

      setSearchedUser({
        wallet: userInfo[0],
        id: userInfo[1].toString(),
        referrer: userInfo[2],
        referrerId: userInfo[3].toString(),
        totalStaked: ethers.utils.formatEther(userInfo[4]),
        totalReferrals: userInfo[5].toString(),
        teamVolume: ethers.utils.formatEther(userInfo[6]),
        directReferrals: userInfo[7].toString(),
        roiEnabled: userInfo[8],
        isBlacklisted: userInfo[9],
        investments: investments.length,
        totalIncome: ethers.utils.formatEther(incomes[3])
      });

    } catch (error) {
      console.error('User search failed:', error);
      showError('User search failed: ' + error.message);
      setSearchedUser(null);
    } finally {
      setLoading(false);
    }
  };

  const updatePlanROI = async () => {
    if (!planUpdateForm.roiPercentage || !stakingContract) return;

    try {
      setLoading(true);
      setError('');

      // Convert percentage to basis points (multiply by 100)
      // e.g., 2.5% becomes 250 basis points
      const roiInBasisPoints = Math.round(parseFloat(planUpdateForm.roiPercentage) * 100);

      const tx = await stakingContract.updatePlanROI(
        planUpdateForm.planId,
        roiInBasisPoints
      );

      showSuccess('ROI update transaction sent...');
      await tx.wait();
      showSuccess('Plan ROI updated successfully!');

      setPlanUpdateForm({ ...planUpdateForm, roiPercentage: '' });

    } catch (error) {
      console.error('ROI update failed:', error);
      showError('ROI update failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateReferralCommission = async () => {
    if (!commissionForm.commission || !stakingContract) return;

    try {
      setLoading(true);
      setError('');

      const tx = await stakingContract.updateReferralCommission(
        commissionForm.planId,
        commissionForm.commission
      );

      showSuccess('Commission update transaction sent...');
      await tx.wait();
      showSuccess('Referral commission updated successfully!');

      setCommissionForm({ ...commissionForm, commission: '' });

    } catch (error) {
      console.error('Commission update failed:', error);
      showError('Commission update failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateLevelCommission = async () => {
    if (!levelCommissionForm.percentage || !stakingContract) return;

    try {
      setLoading(true);
      setError('');

      // Convert percentage to basis points (multiply by 100)
      // e.g., 1% becomes 100 basis points, 0.5% becomes 50 basis points
      const percentageInBasisPoints = Math.round(parseFloat(levelCommissionForm.percentage) * 100);

      const tx = await stakingContract.updateLevelCommission(
        levelCommissionForm.level,
        percentageInBasisPoints
      );

      showSuccess('Level commission update transaction sent...');
      await tx.wait();
      showSuccess('Level commission updated successfully!');

      setLevelCommissionForm({ ...levelCommissionForm, percentage: '' });

    } catch (error) {
      console.error('Level commission update failed:', error);
      showError('Level commission update failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserBlacklist = async () => {
    if (!blacklistForm.userId || !stakingContract) return;

    try {
      setLoading(true);
      setError('');

      const tx = await stakingContract.blacklistUser(
        blacklistForm.userId,
        blacklistForm.action
      );

      showSuccess('Blacklist transaction sent...');
      await tx.wait();
      showSuccess(`User ${blacklistForm.action ? 'blacklisted' : 'unblacklisted'} successfully!`);

      setBlacklistForm({ userId: '', action: true });
      await loadAdminData();

    } catch (error) {
      console.error('Blacklist operation failed:', error);
      showError('Blacklist operation failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserROI = async () => {
    if (!userRoiForm.userId || !stakingContract) return;

    try {
      setLoading(true);
      setError('');

      const tx = await stakingContract.toggleUserROI(
        userRoiForm.userId,
        userRoiForm.enabled
      );

      showSuccess('User ROI toggle transaction sent...');
      await tx.wait();
      showSuccess(`User ROI ${userRoiForm.enabled ? 'enabled' : 'disabled'} successfully!`);

      setUserRoiForm({ userId: '', enabled: true });

    } catch (error) {
      console.error('User ROI toggle failed:', error);
      showError('User ROI toggle failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateTokenPrice = async () => {
    if (!tokenPriceForm.price || !stakingContract) return;

    try {
      setLoading(true);
      setError('');

      const priceInWei = ethers.utils.parseEther(tokenPriceForm.price);
      const tx = await stakingContract.updateTokenPrice(priceInWei);

      showSuccess('Token price update transaction sent...');
      await tx.wait();
      showSuccess('Token price updated successfully!');

      setTokenPriceForm({ price: '' });
      await loadAdminData();

    } catch (error) {
      console.error('Token price update failed:', error);
      showError('Token price update failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const emergencyWithdraw = async () => {
    if (!emergencyForm.amount || !stakingContract) return;

    const confirmMessage = `Are you sure you want to withdraw ${emergencyForm.amount} TRAIL tokens from the contract?\n\nThis is an emergency function and should only be used when necessary.`;
    if (!window.confirm(confirmMessage)) return;

    try {
      setLoading(true);
      setError('');

      const amountInWei = ethers.utils.parseEther(emergencyForm.amount);
      const tx = await stakingContract.emergencyWithdraw(
        currentNetwork.contracts.token,
        amountInWei
      );

      showSuccess('Emergency withdrawal transaction sent...');
      await tx.wait();
      showSuccess('Emergency withdrawal completed successfully!');

      setEmergencyForm({ amount: '' });
      await loadAdminData();

    } catch (error) {
      console.error('Emergency withdrawal failed:', error);
      showError('Emergency withdrawal failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePromotionalRewards = async () => {
    try {
      setLoading(true);
      setError('');

      const tx = await stakingContract.togglePromotionalRewards(!promotionalRewardsEnabled);
      showSuccess('Promotional rewards toggle transaction sent...');
      await tx.wait();
      showSuccess('Promotional rewards settings updated!');

      await loadAdminData();

    } catch (error) {
      console.error('Toggle failed:', error);
      showError('Toggle failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePlanActive = async () => {
    if (!stakingContract) return;

    try {
      setLoading(true);
      setError('');

      const tx = await stakingContract.togglePlan(
        planToggleForm.planId,
        planToggleForm.isActive
      );

      showSuccess('Plan toggle transaction sent...');
      await tx.wait();
      showSuccess(`Plan ${planToggleForm.planId} ${planToggleForm.isActive ? 'enabled' : 'disabled'} for new joins!`);

    } catch (error) {
      console.error('Plan toggle failed:', error);
      showError('Plan toggle failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateRiaReferralPercent = async () => {
    if (!riaSettingsForm.referralPercent || !stakingContract) return;

    try {
      setLoading(true);
      setError('');

      const tx = await stakingContract.setRiaReferralPercent(
        Math.round(parseFloat(riaSettingsForm.referralPercent))
      );

      showSuccess('RIA referral % update transaction sent...');
      await tx.wait();
      showSuccess('RIA referral percentage updated successfully!');

      setRiaSettingsForm({ ...riaSettingsForm, referralPercent: '' });
      await loadAdminData();

    } catch (error) {
      console.error('RIA referral % update failed:', error);
      showError('RIA referral % update failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateRiaThresholdMultiplier = async () => {
    if (!riaSettingsForm.thresholdMultiplier || !stakingContract) return;

    try {
      setLoading(true);
      setError('');

      const tx = await stakingContract.setRiaThresholdMultiplier(
        Math.round(parseFloat(riaSettingsForm.thresholdMultiplier))
      );

      showSuccess('RIA threshold update transaction sent...');
      await tx.wait();
      showSuccess('RIA entry multiplier updated successfully!');

      setRiaSettingsForm({ ...riaSettingsForm, thresholdMultiplier: '' });
      await loadAdminData();

    } catch (error) {
      console.error('RIA threshold update failed:', error);
      showError('RIA threshold update failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'users', label: 'User Management', icon: '👥' },
    { id: 'plans', label: 'Plan Settings', icon: '⚙️' },
    { id: 'levels', label: 'Level Commission', icon: '🏆' },
    { id: 'blacklist', label: 'Blacklist', icon: '🚫' },
    { id: 'system', label: 'System', icon: '🔧' },
    { id: 'emergency', label: 'Emergency', icon: '🚨' }
  ];

  const renderOverview = () => (
    <div className="admin-overview">
      <h3>📊 Admin Dashboard Overview</h3>
      
      {adminStats && (
        <div className="admin-stats-grid">
          <div className="admin-stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <span className="stat-value">{adminStats.totalUsers}</span>
              <span className="stat-label">Total Users</span>
              <span className="stat-detail">{adminStats.activeUsers} active</span>
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <span className="stat-value">{formatNumber(adminStats.totalStaked, 0)}</span>
              <span className="stat-label">Total Staked (USD)</span>
              <span className="stat-detail">User investments</span>
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="stat-icon">💳</div>
            <div className="stat-content">
              <span className="stat-value">{formatNumber(contractBalance, 0)}</span>
              <span className="stat-label">Contract Balance (TRAIL)</span>
              <span className="stat-detail">Available tokens</span>
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="stat-icon">🚫</div>
            <div className="stat-content">
              <span className="stat-value">{blacklistedUsers.length}</span>
              <span className="stat-label">Blacklisted Users</span>
              <span className="stat-detail">Suspended accounts</span>
            </div>
          </div>
        </div>
      )}

      <div className="system-status">
        <h4>🔧 System Status</h4>
        <div className="status-grid">
          <div className="status-item">
            <span className="status-label">Promotional Rewards:</span>
            <span className={`status-badge ${promotionalRewardsEnabled ? 'active' : ''}`}>
              {promotionalRewardsEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <div className="status-item">
            <span className="status-label">Current Token Price:</span>
            <span className="status-value">${formatNumber(currentTokenPrice, 2)}</span>
          </div>
          <div className="status-item">
            <span className="status-label">Total Plans:</span>
            <span className="status-value">{adminStats?.totalPlans || 0}</span>
          </div>
          <div className="status-item">
            <span className="status-label">RIA Referral %:</span>
            <span className="status-value">{riaReferralPercent}%</span>
          </div>
          <div className="status-item">
            <span className="status-label">RIA Entry Multiplier:</span>
            <span className="status-value">{riaThresholdMultiplier}x</span>
          </div>
        </div>
      </div>

      <div className="quick-actions">
        <h4>🚀 Quick Actions</h4>
        <div className="action-buttons-grid">
          <button 
            className="admin-action-btn toggle"
            onClick={togglePromotionalRewards}
            disabled={loading}
          >
            <span className="btn-icon">🎁</span>
            {promotionalRewardsEnabled ? 'Disable' : 'Enable'} Promotions
          </button>
          
          <button 
            className="admin-action-btn refresh"
            onClick={loadAdminData}
            disabled={loading}
          >
            <span className="btn-icon">🔄</span>
            Refresh Data
          </button>
        </div>
      </div>
    </div>
  );

  const renderUsers = () => (
    <div className="user-management">
      <h3>👥 User Management</h3>
      
      <div className="user-search">
        <h4>🔍 Search User</h4>
        <div className="search-form">
          <div className="input-group">
            <input
              type="text"
              placeholder="Enter User ID"
              value={searchUserId}
              onChange={(e) => setSearchUserId(e.target.value)}
              className="admin-input"
            />
            <button 
              className="search-btn"
              onClick={searchUser}
              disabled={loading}
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>

        {searchedUser && (
          <div className="user-details">
            <h5>User Details</h5>
            <div className="user-info-grid">
              <div className="user-info-item">
                <span className="info-label">User ID:</span>
                <span className="info-value">{searchedUser.id}</span>
              </div>
              <div className="user-info-item">
                <span className="info-label">Wallet:</span>
                <span className="info-value">{formatAddress(searchedUser.wallet)}</span>
              </div>
              <div className="user-info-item">
                <span className="info-label">Total Staked:</span>
                <span className="info-value">{formatNumber(searchedUser.totalStaked, 2)} USD</span>
              </div>
              <div className="user-info-item">
                <span className="info-label">Direct Referrals:</span>
                <span className="info-value">{searchedUser.directReferrals}</span>
              </div>
              <div className="user-info-item">
                <span className="info-label">Team Volume:</span>
                <span className="info-value">{formatNumber(searchedUser.teamVolume, 0)} USD</span>
              </div>
              <div className="user-info-item">
                <span className="info-label">Total Income:</span>
                <span className="info-value">{formatNumber(searchedUser.totalIncome, 2)} USD</span>
              </div>
              <div className="user-info-item">
                <span className="info-label">ROI Enabled:</span>
                <span className={`status-badge ${searchedUser.roiEnabled ? 'active' : ''}`}>
                  {searchedUser.roiEnabled ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="user-info-item">
                <span className="info-label">Blacklisted:</span>
                <span className={`status-badge ${searchedUser.isBlacklisted ? 'blacklisted' : 'normal'}`}>
                  {searchedUser.isBlacklisted ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="user-roi-toggle">
        <h4>⚡ Toggle User ROI</h4>
        <div className="form-grid">
          <input
            type="text"
            placeholder="Enter User ID"
            value={userRoiForm.userId}
            onChange={(e) => setUserRoiForm({...userRoiForm, userId: e.target.value})}
            className="admin-input"
          />
          <select 
            value={userRoiForm.enabled}
            onChange={(e) => setUserRoiForm({...userRoiForm, enabled: e.target.value === 'true'})}
            className="admin-select"
          >
            <option value={true}>Enable ROI</option>
            <option value={false}>Disable ROI</option>
          </select>
          <button 
            className="admin-btn primary"
            onClick={toggleUserROI}
            disabled={loading}
          >
            Toggle ROI
          </button>
        </div>
      </div>
    </div>
  );

  const renderPlans = () => (
    <div className="plan-management">
      <h3>⚙️ Plan Management</h3>
      
      <div className="plan-forms">
        <div className="plan-form">
          <h4>📈 Update Plan ROI</h4>
          <div className="form-grid">
            <select 
              value={planUpdateForm.planId}
              onChange={(e) => setPlanUpdateForm({...planUpdateForm, planId: parseInt(e.target.value)})}
              className="admin-select"
            >
              {PLANS.map(plan => (
                <option key={plan.id} value={plan.id}>
                  Plan {plan.id}: {plan.name}
                </option>
              ))}
            </select>
            <input
              type="number"
              placeholder="New ROI Percentage (e.g., 2.5 for 2.5%)"
              value={planUpdateForm.roiPercentage}
              onChange={(e) => setPlanUpdateForm({...planUpdateForm, roiPercentage: e.target.value})}
              className="admin-input"
              step="0.1"
              min="0"
              max="100"
            />
            <button 
              className="admin-btn primary"
              onClick={updatePlanROI}
              disabled={loading}
            >
              Update ROI
            </button>
          </div>
          <p className="form-help">
            Enter percentage directly: 2.5 = 2.5%, 0.19 = 0.19%
          </p>
        </div>

        <div className="plan-form">
          <h4>💰 Update Referral Commission</h4>
          <div className="form-grid">
            <select 
              value={commissionForm.planId}
              onChange={(e) => setCommissionForm({...commissionForm, planId: parseInt(e.target.value)})}
              className="admin-select"
            >
              {PLANS.map(plan => (
                <option key={plan.id} value={plan.id}>
                  Plan {plan.id}: {plan.name}
                </option>
              ))}
            </select>
            <input
              type="number"
              placeholder="New Commission % (e.g., 6 for 6%)"
              value={commissionForm.commission}
              onChange={(e) => setCommissionForm({...commissionForm, commission: e.target.value})}
              className="admin-input"
            />
            <button
              className="admin-btn primary"
              onClick={updateReferralCommission}
              disabled={loading}
            >
              Update Commission
            </button>
          </div>
        </div>

        <div className="plan-form">
          <h4>🔀 Enable / Disable Plan (new joins)</h4>
          <div className="form-grid">
            <select
              value={planToggleForm.planId}
              onChange={(e) => setPlanToggleForm({ ...planToggleForm, planId: parseInt(e.target.value) })}
              className="admin-select"
            >
              {PLANS.map(plan => (
                <option key={plan.id} value={plan.id}>
                  Plan {plan.id}: {plan.name}
                </option>
              ))}
            </select>
            <select
              value={planToggleForm.isActive}
              onChange={(e) => setPlanToggleForm({ ...planToggleForm, isActive: e.target.value === 'true' })}
              className="admin-select"
            >
              <option value={true}>Enable Plan</option>
              <option value={false}>Disable Plan</option>
            </select>
            <button
              className="admin-btn primary"
              onClick={togglePlanActive}
              disabled={loading}
            >
              {planToggleForm.isActive ? 'Enable' : 'Disable'} Plan
            </button>
          </div>
          <p className="form-help">
            Disabling a plan only blocks new investments. Existing investments keep earning and can still withdraw.
          </p>
        </div>
      </div>
    </div>
  );

  const renderLevels = () => (
    <div className="level-management">
      <h3>🏆 Level Commission Management</h3>
      
      <div className="level-form">
        <h4>📊 Update Level Commission</h4>
        <div className="form-grid">
          <select 
            value={levelCommissionForm.level}
            onChange={(e) => setLevelCommissionForm({...levelCommissionForm, level: parseInt(e.target.value)})}
            className="admin-select"
          >
            {Array.from({length: 20}, (_, i) => i + 1).map(level => (
              <option key={level} value={level}>
                Level {level}
              </option>
            ))}
          </select>
          <input
            type="number"
            placeholder="Commission percentage (e.g., 1.0 for 1%)"
            value={levelCommissionForm.percentage}
            onChange={(e) => setLevelCommissionForm({...levelCommissionForm, percentage: e.target.value})}
            className="admin-input"
            step="0.01"
            min="0"
            max="10"
          />
          <button 
            className="admin-btn primary"
            onClick={updateLevelCommission}
            disabled={loading}
          >
            Update Level Commission
          </button>
        </div>
        <p className="form-help">
          Enter percentage directly: 1.0 = 1%, 0.5 = 0.5%, 0.125 = 0.125%
        </p>
      </div>
    </div>
  );

  const renderBlacklist = () => (
    <div className="blacklist-management">
      <h3>🚫 Blacklist Management</h3>
      
      <div className="blacklist-form">
        <h4>⚡ Blacklist/Unblacklist User</h4>
        <div className="form-grid">
          <input
            type="text"
            placeholder="Enter User ID"
            value={blacklistForm.userId}
            onChange={(e) => setBlacklistForm({...blacklistForm, userId: e.target.value})}
            className="admin-input"
          />
          <select 
            value={blacklistForm.action}
            onChange={(e) => setBlacklistForm({...blacklistForm, action: e.target.value === 'true'})}
            className="admin-select"
          >
            <option value={true}>Blacklist User</option>
            <option value={false}>Unblacklist User</option>
          </select>
          <button 
            className="admin-btn danger"
            onClick={toggleUserBlacklist}
            disabled={loading}
          >
            {blacklistForm.action ? 'Blacklist' : 'Unblacklist'}
          </button>
        </div>
      </div>

      <div className="blacklisted-users">
        <h4>📋 Blacklisted Users ({blacklistedUsers.length})</h4>
        {blacklistedUsers.length > 0 ? (
          <div className="blacklisted-list">
            {blacklistedUsers.map(userId => (
              <div key={userId} className="blacklisted-item">
                <span className="user-id">ID: {userId}</span>
                <button 
                  className="unblacklist-btn"
                  onClick={() => {
                    setBlacklistForm({ userId, action: false });
                    toggleUserBlacklist();
                  }}
                  disabled={loading}
                >
                  Unblacklist
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="empty-message">No blacklisted users</p>
        )}
      </div>
    </div>
  );

  const renderSystem = () => (
    <div className="system-management">
      <h3>🔧 System Management</h3>
      
      <div className="system-form">
        <h4>💲 Update Token Price</h4>
        <div className="form-grid">
          <input
            type="number"
            placeholder="New token price in USD (e.g., 2.50)"
            value={tokenPriceForm.price}
            onChange={(e) => setTokenPriceForm({price: e.target.value})}
            className="admin-input"
            step="0.01"
          />
          <button 
            className="admin-btn primary"
            onClick={updateTokenPrice}
            disabled={loading}
          >
            Update Token Price
          </button>
        </div>
        <p className="form-help">Current token price: ${formatNumber(currentTokenPrice, 2)} per TRAIL</p>
        <p className="form-help">This affects how many tokens users need for USD investments</p>
      </div>

      <div className="system-form">
        <h4>🔁 RIA (Retopup) Settings</h4>
        <div className="form-grid">
          <input
            type="number"
            placeholder={`RIA referral % (current: ${riaReferralPercent}%)`}
            value={riaSettingsForm.referralPercent}
            onChange={(e) => setRiaSettingsForm({ ...riaSettingsForm, referralPercent: e.target.value })}
            className="admin-input"
            step="1"
            min="0"
            max="100"
          />
          <button
            className="admin-btn primary"
            onClick={updateRiaReferralPercent}
            disabled={loading}
          >
            Update RIA Referral %
          </button>
        </div>
        <p className="form-help">Direct referral % paid on each RIA retopup. Current: {riaReferralPercent}%</p>

        <div className="form-grid">
          <input
            type="number"
            placeholder={`RIA entry multiplier (current: ${riaThresholdMultiplier}x)`}
            value={riaSettingsForm.thresholdMultiplier}
            onChange={(e) => setRiaSettingsForm({ ...riaSettingsForm, thresholdMultiplier: e.target.value })}
            className="admin-input"
            step="1"
            min="1"
          />
          <button
            className="admin-btn primary"
            onClick={updateRiaThresholdMultiplier}
            disabled={loading}
          >
            Update RIA Multiplier
          </button>
        </div>
        <p className="form-help">
          RIA mode activates once cumulative income reaches this multiple of the first investment. Current: {riaThresholdMultiplier}x
        </p>
      </div>
    </div>
  );

  const renderEmergency = () => (
    <div className="emergency-management">
      <h3>🚨 Emergency Functions</h3>
      
      <div className="emergency-form">
        <h4>💸 Emergency Withdraw</h4>
        <p className="warning-text">
          ⚠️ This function should only be used in emergency situations!
        </p>
        <div className="form-grid">
          <input
            type="number"
            placeholder="Amount to withdraw (TRAIL tokens)"
            value={emergencyForm.amount}
            onChange={(e) => setEmergencyForm({amount: e.target.value})}
            className="admin-input"
            step="0.01"
          />
          <button 
            className="admin-btn danger"
            onClick={emergencyWithdraw}
            disabled={loading}
          >
            Emergency Withdraw
          </button>
        </div>
        
        <div className="emergency-info">
          <div className="info-item">
            <span className="info-label">Contract Balance:</span>
            <span className="info-value">{formatNumber(contractBalance, 2)} TRAIL</span>
          </div>
          <div className="info-item">
            <span className="info-label">USD Equivalent:</span>
            <span className="info-value">${formatNumber(parseFloat(contractBalance) * parseFloat(currentTokenPrice), 2)}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="admin-panel">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-title">
          <h2>🔑 Admin Panel</h2>
          <p>Contract management and system controls</p>
        </div>
        <div className="admin-badge">
          <span className="badge-icon">⚡</span>
          <span>Admin Access</span>
        </div>
      </div>

      {/* Alerts */}
      {success && (
        <div className="admin-alert success">
          <span className="alert-icon">✅</span>
          {success}
        </div>
      )}

      {error && (
        <div className="admin-alert error">
          <span className="alert-icon">❌</span>
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="admin-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="admin-content">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'users' && renderUsers()}
        {activeTab === 'plans' && renderPlans()}
        {activeTab === 'levels' && renderLevels()}
        {activeTab === 'blacklist' && renderBlacklist()}
        {activeTab === 'system' && renderSystem()}
        {activeTab === 'emergency' && renderEmergency()}
      </div>
    </div>
  );
};

export default AdminPanel;